package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.extensions.wrapWithParentheses
import com.eladfinish.bible.contest.list_item_secondary_text
import com.eladfinish.bible.contest.onqelos
import com.eladfinish.bible.contest.screens.search.model.ResultsJump
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.appScrollbarStyle
import com.eladfinish.bible.contest.ui.components.fitsTrack
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import dev.icerock.moko.resources.compose.colorResource
import io.github.oikvpqya.compose.fastscroller.VerticalScrollbar
import io.github.oikvpqya.compose.fastscroller.rememberScrollbarAdapter
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.coroutines.delay

private const val JUMP_HIGHLIGHT_MILLIS = 1_500L
private const val JUMP_HIGHLIGHT_FADE_MILLIS = 600

@Composable
internal fun SearchResultsList(
    items: ImmutableList<VerseUiModel>,
    onItemClick: (VerseUiModel) -> Unit,
    onItemLongClick: (VerseUiModel) -> Unit,
    jump: ResultsJump? = null,
    onJumpHandled: () -> Unit = {},
) {
    val state = rememberLazyListState()
    var highlightedId by remember { mutableStateOf<String?>(null) }
    val currentOnJumpHandled by rememberUpdatedState(onJumpHandled)

    // No animation: the jump can cross thousands of results. Near the end of the list the item
    // cannot reach the top, so the highlight is what shows where the jump landed. A jump made for
    // results that have been replaced since is dropped.
    LaunchedEffect(jump) {
        if (jump == null) return@LaunchedEffect
        if (items.getOrNull(jump.index)?.id == jump.resultId) {
            state.scrollToItem(jump.index)
            highlightedId = jump.resultId
        }
        currentOnJumpHandled()
    }

    LaunchedEffect(highlightedId) {
        if (highlightedId != null) {
            delay(JUMP_HIGHLIGHT_MILLIS)
            highlightedId = null
        }
    }

    Box(Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(start = 10.dp),
            state = state,
            contentPadding = navigationBarContentPadding(),
        ) {
            itemsIndexed(
                items = items,
                key = { _, item -> item.id },
            ) { index, item ->
                SearchResultItem(
                    item = item,
                    number = index.plus(1).toString(),
                    isHighlighted = item.id == highlightedId,
                    onItemClick = onItemClick,
                    onItemLongClick = onItemLongClick,
                )

                if (index < items.lastIndex) {
                    HorizontalDivider(
                        modifier = Modifier.padding(vertical = 2.dp),
                    )
                }
            }
        }

        Scroller(
            state = state,
            modifier = Modifier
                .align(Alignment.TopStart),
        )
    }
}

/**
 * The scrollbar exists only while the list overflows and is tall enough for its thumb (see
 * [fitsTrack]): the list and the scrollbar fill the same box, so the list's viewport is the
 * scrollbar's track. Leaving the composition also ends a drag that is in progress when the list
 * shrinks under the finger (the keyboard opening), the case that used to crash.
 */
@Composable
private fun Scroller(
    state: LazyListState,
    modifier: Modifier = Modifier,
) {
    val style = appScrollbarStyle()
    val viewportHeight by remember(state) { derivedStateOf { state.layoutInfo.viewportSize.height } }
    val canScroll = state.canScrollForward || state.canScrollBackward
    if (!canScroll || !style.fitsTrack(viewportHeight)) return

    VerticalScrollbar(
        modifier = modifier.fillMaxHeight(),
        adapter = rememberScrollbarAdapter(scrollState = state),
        style = style,
        enablePressToScroll = false,
    )
}

@Composable
private fun SearchResultItem(
    item: VerseUiModel,
    number: String,
    isHighlighted: Boolean = false,
    onItemClick: (VerseUiModel) -> Unit = {},
    onItemLongClick: (VerseUiModel) -> Unit = {},
) {
    val haptic = LocalHapticFeedback.current
    val background by animateColorAsState(
        targetValue = if (isHighlighted) MaterialTheme.colorScheme.primary.copy(alpha = 0.18f) else Color.Transparent,
        animationSpec = tween(durationMillis = JUMP_HIGHLIGHT_FADE_MILLIS),
    )

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(background)
            .padding(5.dp)
            .combinedClickable(
                onClick = {
                    onItemClick(item)
                },
                onLongClick = {
                    haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                    onItemLongClick(item)
                },
            ),
    ) {
        val annotatedString = buildAnnotatedString {
            append(item.text)

            item.highlightedRanges.forEach { range ->
                val start = range.first.coerceIn(
                    minimumValue = 0,
                    maximumValue = item.text.length,
                )
                val end = range.last.plus(1).coerceIn(
                    minimumValue = 0,
                    maximumValue = item.text.length,
                )

                if (start < end) {
                    addStyle(
                        style = SpanStyle(background = Color.Yellow),
                        start = start,
                        end = end,
                    )
                }
            }
        }

        Text(
            text = annotatedString,
            color = Color(0xFF202020),
            fontSize = 19.sp,
        )

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 4.dp),
        ) {
            Text(
                text = item.source,
                modifier = Modifier.weight(1f),
                fontSize = 18.sp,
                color = colorResource(SharedRes.colors.onqelos),
            )

            Text(
                text = number.wrapWithParentheses(),
                modifier = Modifier.wrapContentWidth(),
                textAlign = TextAlign.End,
                fontSize = 18.sp,
                color = colorResource(SharedRes.colors.list_item_secondary_text),
            )
        }
    }
}

@Preview(showBackground = true)
@Composable
private fun SearchResultItemPreview() {
    SearchResultItem(
        item = VerseUiModel(
            id = "preview_1",
            text = "בְּרֵאשִׁ֖ית בָּרָ֣א אֱלֹהִ֑ים אֵ֥ת הַשָּׁמַ֖יִם וְאֵ֥ת הָאָֽרֶץ",
            source = "(בראשית א, א)",
            highlightedRanges = persistentListOf(
                0..11,
                56..63,
            ),
            bookIndex = 0,
            chapterIndex = 1,
            verse = "א",
        ),
        number = "1",
    )
}

@Preview(showBackground = true)
@Composable
private fun SearchResultsListPreview() {
    PreviewTheme {
        SearchResultsList(
            items = persistentListOf(
                VerseUiModel(
                    id = "preview_1",
                    text = "בְּרֵאשִׁ֖ית בָּרָ֣א אֱלֹהִ֑ים אֵ֥ת הַשָּׁמַ֖יִם וְאֵ֥ת הָאָֽרֶץ",
                    source = "(בראשית א, א)",
                    highlightedRanges = persistentListOf(0..11),
                    bookIndex = 0,
                    chapterIndex = 1,
                    verse = "א",
                ),
                VerseUiModel(
                    id = "preview_2",
                    text = "וַיֹּ֥אמֶר אֱלֹהִ֖ים יְהִ֣י א֑וֹר וַֽיְהִי־אֽוֹר",
                    source = "(בראשית א, ג)",
                    highlightedRanges = persistentListOf(11..19),
                    bookIndex = 0,
                    chapterIndex = 1,
                    verse = "ג",
                ),
            ),
            onItemClick = {},
            onItemLongClick = {},
        )
    }
}
